import { useId, type ComponentPropsWithRef } from "react";
import { cx } from "../../cx";
import { ChevronDownIcon } from "../../rtl/icons";
import { inputStateClasses } from "../Input/Input";

export interface SelectOption {
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean;
}

export interface SelectProps extends Omit<ComponentPropsWithRef<"select">, "size" | "children"> {
  /** Visible label (required for accessibility). */
  readonly label: string;
  readonly hideLabel?: boolean;
  readonly options: readonly SelectOption[];
  readonly hint?: string;
  /** Error message; also sets `aria-invalid`. */
  readonly error?: string;
  readonly containerClassName?: string;
}

/**
 * Native `<select>` styled like `Input` (same tokens, focus ring and error state). The chevron
 * sits at the inline end, so it moves to the left in Arabic without direction-specific code.
 */
export function Select({
  label,
  hideLabel = false,
  options,
  hint,
  error,
  id,
  className,
  containerClassName,
  ...rest
}: SelectProps) {
  const autoId = useId();
  const selectId = id ?? `select-${autoId}`;
  const hintId = hint ? `${selectId}-hint` : undefined;
  const errorId = error ? `${selectId}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(" ") || undefined;

  return (
    <div className={cx("flex flex-col gap-1.5", containerClassName)}>
      <label
        htmlFor={selectId}
        className={cx("text-sm font-medium text-fg", hideLabel && "sr-only")}
      >
        {label}
      </label>
      <div className="relative">
        <select
          id={selectId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cx(
            "block h-11 w-full appearance-none rounded-xl border ps-3 pe-10 text-sm transition-colors",
            "focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-focus",
            "disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:opacity-70",
            error ? inputStateClasses.error : inputStateClasses.base,
            className,
          )}
          {...rest}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
        <span className="pointer-events-none absolute inset-y-0 end-0 flex items-center pe-3 text-fg-muted">
          <ChevronDownIcon className="size-4" />
        </span>
      </div>
      {error && (
        <p id={errorId} className="text-xs text-danger-fg">
          {error}
        </p>
      )}
      {hint && (
        <p id={hintId} className="text-xs text-fg-muted">
          {hint}
        </p>
      )}
    </div>
  );
}
